import { createSelector } from "reselect";
import Contribution from "../../models/Contribution";
import getPathogenicityColor from "../../common/helpers/getPathogenicityColor";
import getPathogenicityName from "../../common/helpers/getPathogenicityName";
import groupBy from "../../common/utils/groupBy";
import Organisation from "../../models/Organisation";
import User from "../../models/User";

const getStateSelector = (state) => state;

const getVariantResultSelector = createSelector(
  getStateSelector,
  (state) => state.variantResult
);

const getPubmedEntriesSelector = createSelector(
  getVariantResultSelector,
  (result) => (result ? result.pub_med_user_entries : []) || []
);

const getPubmedEntriesPerArticle = createSelector(
  getVariantResultSelector,
  getPubmedEntriesSelector,
  (variantResult, entries) => {
    return entries.filter(Boolean).reduce((a, i) => {
      const refs = i.pub_med_references || [];
      const ref = refs[0];
      const id = ref ? `${ref}` : "";
      const org = new Organisation({ name: i.userOrganisation });
      const fullName = i.user ? i.user.full_name : "";
      const screenName = i.user ? i.user.screen_name : "";
      const user = new User({ ...i.user, fullName, screenName }, org);
      const c = new Contribution(i)
        .setUser(user)
        .setVariantId(variantResult?.variant_id);

      const existing = a.get(id) || [];
      return a.set(id, [...existing, c]);
    }, new Map());
  }
);

const getPubmedIdsForUserEntries = createSelector(
  getPubmedEntriesPerArticle,
  (articlesToEntriesMap) => Array.from(articlesToEntriesMap.keys())
);

const getReferencedByUsersPerArticle = createSelector(
  getPubmedEntriesPerArticle,
  (articleToEntryMap) => {
    return Array.from(articleToEntryMap.keys())
      .map((key) => ({ k: key, v: articleToEntryMap.get(key) }))
      .map(({ k, v }) => ({ k, v: groupBy(v, (c) => c.user.screenName) }))
      .reduce((a, i) => ({ ...a, [i.k]: i.v }), {});
  }
);

const getPathogenicitiesPerArticle = createSelector(
  getVariantResultSelector,
  getPubmedEntriesPerArticle,
  (variantResult, articleToEntryMap) => {
    return Array.from(articleToEntryMap.keys())
      .map((key) => ({
        key,
        vals: articleToEntryMap.get(key),
      }))
      .map(({ key, vals }) => {
        const aggregated = vals
          .filter((x) => x.pathogenicity)
          .reduce((a, x, i) => {
            const existing = a.get(x.pathogenicity);
            return a.set(x.pathogenicity, {
              pathogenicity: x.pathogenicity,
              id: `${i}-${new Date().getTime()}`,
              times: (existing ? existing.times : 0) + 1,
              users: [
                ...(existing ? existing.users : []),
                { ...x.user, coAuthor: x.coAuthor },
              ],
            });
          }, new Map());

        const pathogenicities = Array.from(aggregated.values());

        return {
          key,
          vals: pathogenicities.map((x) => ({
            ...x,
            variantId: String(variantResult?.variant_id),
            name: getPathogenicityName(x.pathogenicity),
            color: getPathogenicityColor(x.pathogenicity),
            users: groupBy(x.users.filter(Boolean), (u) => u.ssoId).map(
              (l) => ({
                ssoId: l.values[0].ssoId,
                coAuthor: l.values[0].coAuthor,
              })
            ),
          })),
        };
      })
      .reduce((a, i) => ({ ...a, [i.key]: i.vals }), {});
  }
);

export {
  getPubmedEntriesPerArticle,
  getPubmedIdsForUserEntries,
  getPathogenicitiesPerArticle,
  getReferencedByUsersPerArticle,
};
